<template>
    <div ref="containerRef" class="w-panel resizable-columns" :class="$attrs.class">
        <slot></slot>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    storageKey: {
        type: String,
        required: true
    },
    minColumnWidth: {
        type: Number,
        default: 275
    },
    // When false (e.g. no detail panel is open, only a placeholder), stored
    // widths are not applied so the columns fall back to their natural flex.
    active: {
        type: Boolean,
        default: true
    }
});

const containerRef = ref(null);
let handles = [];
let columnEls = [];
let observer = null;
let resizeObserver = null;

// Remembers each column's flex value before we overwrite it with a pixel width,
// so we can put it back when stored widths should no longer apply.
const originalFlex = new WeakMap();

function captureOriginal(el) {
    if (!originalFlex.has(el)) {
        originalFlex.set(el, el.style.flex || '');
    }
}

// Desktop breakpoint (matches $desktop in variables.scss)
const DESKTOP_BREAKPOINT = 960;
const isDesktop = ref(window.innerWidth >= DESKTOP_BREAKPOINT);

const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);

function onMediaChange(e) {
    isDesktop.value = e.matches;
    if (isDesktop.value && props.active) {
        initResizeHandles();
        restoreWidths();
    } else {
        removeHandles();
        clearInlineWidths();
    }
}

mq.addEventListener('change', onMediaChange);

// Re-apply or release stored widths as the detail panel opens/closes.
watch(
    () => props.active,
    active => {
        if (!isDesktop.value) return;
        if (active) {
            initResizeHandles();
            restoreWidths();
        } else {
            removeHandles();
            clearInlineWidths();
        }
    }
);

onMounted(() => {
    nextTick(() => {
        if (isDesktop.value && props.active) {
            initResizeHandles();
            restoreWidths();
        }
        observeChildren();
        initResizeObserver();
    });
});

onBeforeUnmount(() => {
    removeHandles();
    mq.removeEventListener('change', onMediaChange);
    if (observer) {
        observer.disconnect();
        observer = null;
    }
    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
});

function getColumnElements() {
    const container = containerRef.value;
    if (!container) return [];
    return Array.from(container.children).filter(el => !el.classList.contains('resize-grip'));
}

function removeHandles() {
    handles.forEach(h => h.remove());
    handles = [];
}

function clearInlineWidths() {
    // Only restore columns we actually overwrote with a pixel width. Touching
    // untouched columns would wipe their component-bound inline flex (e.g.
    // BaseList's list-flex/preview-flex), which the framework won't re-apply.
    columnEls.forEach(el => {
        if (originalFlex.has(el)) {
            el.style.flex = originalFlex.get(el);
        }
    });
}

function initResizeHandles() {
    removeHandles();

    const container = containerRef.value;
    if (!container) return;

    columnEls = getColumnElements();

    // Need at least 2 columns for a grip
    if (columnEls.length < 2) return;

    for (let i = 0; i < columnEls.length - 1; i++) {
        const grip = document.createElement('div');
        grip.className = 'resize-grip';
        grip.addEventListener('pointerdown', e => startResize(e, i));

        // Insert grip before the next column
        container.insertBefore(grip, columnEls[i + 1]);
        handles.push(grip);
    }
}

function startResize(event, index) {
    event.preventDefault();

    // Re-read columns in case DOM changed
    columnEls = getColumnElements();
    if (index >= columnEls.length - 1) return;

    const leftCol = columnEls[index];
    const rightCol = columnEls[index + 1];
    captureOriginal(leftCol);
    captureOriginal(rightCol);
    const startX = event.clientX;
    const leftWidth = leftCol.getBoundingClientRect().width;
    const rightWidth = rightCol.getBoundingClientRect().width;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    // Prevent iframes from stealing pointer events during drag
    const iframes = containerRef.value.querySelectorAll('iframe');
    iframes.forEach(f => (f.style.pointerEvents = 'none'));

    const totalWidth = leftWidth + rightWidth;

    function onMove(e) {
        const dx = e.clientX - startX;
        let newLeft = leftWidth + dx;
        let newRight = rightWidth - dx;

        if (newLeft < props.minColumnWidth) {
            newLeft = props.minColumnWidth;
            newRight = totalWidth - newLeft;
        } else if (newRight < props.minColumnWidth) {
            newRight = props.minColumnWidth;
            newLeft = totalWidth - newRight;
        }

        leftCol.style.flex = `0 0 ${newLeft}px`;
        rightCol.style.flex = `0 0 ${newRight}px`;
    }

    function onUp() {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        iframes.forEach(f => (f.style.pointerEvents = ''));
        saveWidths();
    }

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
}

function saveWidths() {
    const widths = columnEls.map(el => el.getBoundingClientRect().width);
    try {
        localStorage.setItem(`panel-widths-${props.storageKey}`, JSON.stringify(widths));
    } catch (e) {
        // localStorage might be full or disabled
    }
}

// Grip is flex: 0 0 4px (from global.scss)
const GRIP_WIDTH = 4;

function availableWidth() {
    const container = containerRef.value;
    if (!container) return 0;
    const total = container.getBoundingClientRect().width;
    const grips = Array.from(container.children).filter(el => el.classList.contains('resize-grip'));
    return total - grips.length * GRIP_WIDTH;
}

function clampColumnsToContainer(widths) {
    const avail = availableWidth();
    if (!avail) return null;

    const total = widths.reduce((sum, w) => sum + w, 0);
    if (total <= avail) return widths;

    const scale = avail / total;
    const scaled = widths.map(w => w * scale);

    if (scaled.some(w => w < props.minColumnWidth)) return null;
    return scaled;
}

// Make the columns exactly fill the container by giving any leftover width to
// the last column (the detail/preview pane). Without this, stored widths whose
// sum is narrower than the container leave an empty gap on the right.
function fillRemainder(widths) {
    const avail = availableWidth();
    if (!avail) return widths;

    const total = widths.reduce((sum, w) => sum + w, 0);
    const diff = avail - total;
    if (Math.abs(diff) < 1) return widths;

    const result = widths.slice();
    const last = result.length - 1;
    result[last] = Math.max(props.minColumnWidth, result[last] + diff);
    return result;
}

function restoreWidths() {
    const stored = localStorage.getItem(`panel-widths-${props.storageKey}`);
    if (!stored) return;

    try {
        const widths = JSON.parse(stored);
        columnEls = getColumnElements();

        if (widths.length !== columnEls.length) return;

        const clamped = clampColumnsToContainer(widths);
        if (!clamped) {
            localStorage.removeItem(`panel-widths-${props.storageKey}`);
            return;
        }

        const finalWidths = fillRemainder(clamped);

        columnEls.forEach((el, i) => {
            if (finalWidths[i]) {
                captureOriginal(el);
                el.style.flex = `0 0 ${finalWidths[i]}px`;
            }
        });
    } catch (e) {
        // Invalid stored data, ignore
    }
}

function initResizeObserver() {
    const container = containerRef.value;
    if (!container || typeof ResizeObserver === 'undefined') return;

    resizeObserver = new ResizeObserver(() => {
        if (!isDesktop.value || !props.active) return;

        columnEls = getColumnElements();
        if (!columnEls.length) return;

        const currentWidths = columnEls.map(el => {
            const flex = el.style.flex;
            return flex && flex.startsWith('0 0 ') ? parseFloat(flex.slice(4)) : null;
        });

        if (currentWidths.some(w => w === null)) return;

        const clamped = clampColumnsToContainer(currentWidths);
        if (!clamped) {
            clearInlineWidths();
            return;
        }

        // Refit to the container: shrink when overflowing, and grow the last
        // column when there's leftover space, so the detail pane always fills.
        const fitted = fillRemainder(clamped);

        // Only apply if a column actually needs to change
        if (fitted.every((w, i) => Math.abs(w - currentWidths[i]) < 1)) return;

        columnEls.forEach((el, i) => {
            el.style.flex = `0 0 ${fitted[i]}px`;
        });
    });

    resizeObserver.observe(container);
}

function observeChildren() {
    const container = containerRef.value;
    if (!container) return;

    observer = new MutationObserver(() => {
        if (!isDesktop.value || !props.active) return;

        const newColumns = getColumnElements();
        // Reinitialize if column count or elements changed
        const changed = newColumns.length !== columnEls.length || newColumns.some((el, i) => el !== columnEls[i]);
        if (changed) {
            clearInlineWidths();
            initResizeHandles();
            restoreWidths();
        }
    });

    observer.observe(container, { childList: true });
}
</script>
